/**
 * FirestoreService - Firestore implementation of IDatabaseService
 *
 * Wraps Firebase Firestore SDK operations behind a vendor-agnostic interface.
 * Provides type-safe CRUD operations, real-time listeners, batch operations,
 * and transaction support with comprehensive error handling and telemetry.
 *
 * @see WI-013 - Firestore Service Abstraction
 * @see CheckerFly_SDD.md Section 4.5 (Component Responsibilities)
 */

import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  onSnapshot,
  writeBatch as firestoreWriteBatch,
  runTransaction as firestoreRunTransaction,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  Timestamp,
  DocumentData,
  QueryConstraint as FirestoreQueryConstraint,
  DocumentSnapshot,
  QuerySnapshot,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import {
  IDatabaseService,
  QueryConstraint,
  WriteBatch,
  Transaction,
  FieldValueHelpers,
  FirestoreFieldValue,
} from './database.interface';
import { AnalyticsService } from './analytics.service';
import { devLog, devError } from '@core';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService implements IDatabaseService {
  private firestore = inject(Firestore);
  private analytics = inject(AnalyticsService);

  /**
   * Get a single document by path
   * @param path - Document path (e.g., 'users/userId')
   * @returns Promise resolving to document data or null if not found
   */
  async getDoc<T>(path: string): Promise<T | null> {
    const startTime = Date.now();
    try {
      const docRef = doc(this.firestore, path);
      const docSnap = await getDoc(docRef);

      this.trackEvent('firestore.read', {
        collection: this.extractCollection(path),
        doc_id: this.extractDocId(path),
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });

      return docSnap.exists() ? (docSnap.data() as T) : null;
    } catch (error: unknown) {
      this.handleError('getDoc', path, error);
      throw error;
    }
  }

  /**
   * Set a document (create or overwrite)
   * @param path - Document path (e.g., 'users/userId')
   * @param data - Document data
   * @param merge - If true, merge with existing data instead of overwriting
   */
  async setDoc<T>(path: string, data: T, merge: boolean = false): Promise<void> {
    const startTime = Date.now();
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data as DocumentData, { merge });

      this.trackEvent('firestore.write', {
        collection: this.extractCollection(path),
        doc_id: this.extractDocId(path),
        operation: 'set',
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });
    } catch (error: unknown) {
      this.handleError('setDoc', path, error);
      throw error;
    }
  }

  /**
   * Update specific fields in a document
   * @param path - Document path (e.g., 'users/userId')
   * @param data - Partial document data with fields to update
   */
  async updateDoc<T>(path: string, data: Partial<T>): Promise<void> {
    const startTime = Date.now();
    try {
      const docRef = doc(this.firestore, path);
      await updateDoc(docRef, data as DocumentData);

      this.trackEvent('firestore.write', {
        collection: this.extractCollection(path),
        doc_id: this.extractDocId(path),
        operation: 'update',
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });
    } catch (error: unknown) {
      this.handleError('updateDoc', path, error);
      throw error;
    }
  }

  /**
   * Delete a document
   * @param path - Document path (e.g., 'users/userId')
   */
  async deleteDoc(path: string): Promise<void> {
    const startTime = Date.now();
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);

      this.trackEvent('firestore.write', {
        collection: this.extractCollection(path),
        doc_id: this.extractDocId(path),
        operation: 'delete',
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });
    } catch (error: unknown) {
      this.handleError('deleteDoc', path, error);
      throw error;
    }
  }

  /**
   * Get all documents in a collection with optional query constraints
   * @param path - Collection path (e.g., 'users/userId/checkIns')
   * @param queryConstraints - Optional filters, ordering, pagination
   * @returns Promise resolving to array of documents
   */
  async getCollection<T>(
    path: string,
    queryConstraints: QueryConstraint[] = []
  ): Promise<T[]> {
    const startTime = Date.now();
    try {
      const collectionRef = collection(this.firestore, path);
      const firestoreConstraints = this.buildQuery(queryConstraints);
      const q = query(collectionRef, ...firestoreConstraints);
      const querySnap = await getDocs(q);

      this.trackEvent('firestore.read', {
        collection: path,
        doc_count: querySnap.size,
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });

      return querySnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as T));
    } catch (error: unknown) {
      this.handleError('getCollection', path, error);
      throw error;
    }
  }

  /**
   * Add a new document to a collection with auto-generated ID
   * @param collectionPath - Collection path (e.g., 'users/userId/checkIns')
   * @param data - Document data
   * @returns Promise resolving to the new document ID
   */
  async addDoc<T>(collectionPath: string, data: T): Promise<string> {
    const startTime = Date.now();
    try {
      const collectionRef = collection(this.firestore, collectionPath);
      const docRef = await addDoc(collectionRef, data as DocumentData);

      this.trackEvent('firestore.write', {
        collection: collectionPath,
        doc_id: docRef.id,
        operation: 'add',
        timestamp: new Date().toISOString(),
        latency_ms: Date.now() - startTime,
      });

      return docRef.id;
    } catch (error: unknown) {
      this.handleError('addDoc', collectionPath, error);
      throw error;
    }
  }

  /**
   * Create a real-time listener for a single document
   * @param path - Document path (e.g., 'users/userId')
   * @returns Observable that emits document data on changes
   */
  docListener<T>(path: string): Observable<T | null> {
    const docRef = doc(this.firestore, path);
    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnap: DocumentSnapshot) => {
          observer.next(docSnap.exists() ? (docSnap.data() as T) : null);
        },
        (error) => {
          this.handleError('docListener', path, error);
          observer.error(error);
        }
      );
      return () => unsubscribe();
    });
  }

  /**
   * Create a real-time listener for a collection with optional query constraints
   * @param path - Collection path (e.g., 'users/userId/checkIns')
   * @param queryConstraints - Optional filters, ordering, pagination
   * @returns Observable that emits array of documents on changes
   */
  collectionListener<T>(
    path: string,
    queryConstraints: QueryConstraint[] = []
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, path);
    const firestoreConstraints = this.buildQuery(queryConstraints);
    const q = query(collectionRef, ...firestoreConstraints);

    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        q,
        (querySnap: QuerySnapshot) => {
          const data = querySnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          } as T));
          observer.next(data);
        },
        (error) => {
          this.handleError('collectionListener', path, error);
          observer.error(error);
        }
      );
      return () => unsubscribe();
    });
  }

  /**
   * Create a batch write operation
   * @returns WriteBatch interface for batching multiple writes
   */
  batch(): WriteBatch {
    const firestoreBatch = firestoreWriteBatch(this.firestore);
    const operations: Array<{ type: string; path: string }> = [];

    return {
      set: <T>(path: string, data: T, merge = false): void => {
        const docRef = doc(this.firestore, path);
        firestoreBatch.set(docRef, data as DocumentData, { merge });
        operations.push({ type: 'set', path });
      },
      update: <T>(path: string, data: Partial<T>): void => {
        const docRef = doc(this.firestore, path);
        firestoreBatch.update(docRef, data as DocumentData);
        operations.push({ type: 'update', path });
      },
      delete: (path: string): void => {
        const docRef = doc(this.firestore, path);
        firestoreBatch.delete(docRef);
        operations.push({ type: 'delete', path });
      },
      commit: async (): Promise<void> => {
        const startTime = Date.now();
        try {
          await firestoreBatch.commit();
          this.trackEvent('firestore.batch_write', {
            operation_count: operations.length,
            timestamp: new Date().toISOString(),
            latency_ms: Date.now() - startTime,
          });
        } catch (error: unknown) {
          this.handleError('batch.commit', 'batch', error);
          throw error;
        }
      },
    };
  }

  /**
   * Run a transaction with automatic retry on conflicts
   * @param updateFn - Function that receives a Transaction object and performs operations
   * @returns Promise resolving to the transaction result
   */
  async runTransaction<T>(
    updateFn: (transaction: Transaction) => Promise<T>
  ): Promise<T> {
    return firestoreRunTransaction(this.firestore, async (firestoreTransaction) => {
      const wrappedTransaction: Transaction = {
        get: async <T>(path: string): Promise<T | null> => {
          const docRef = doc(this.firestore, path);
          const docSnap = await firestoreTransaction.get(docRef);
          return docSnap.exists() ? (docSnap.data() as T) : null;
        },
        set: <T>(path: string, data: T, merge = false) => {
          const docRef = doc(this.firestore, path);
          firestoreTransaction.set(docRef, data as DocumentData, { merge });
        },
        update: <T>(path: string, data: Partial<T>) => {
          const docRef = doc(this.firestore, path);
          firestoreTransaction.update(docRef, data as DocumentData);
        },
        delete: (path: string) => {
          const docRef = doc(this.firestore, path);
          firestoreTransaction.delete(docRef);
        },
      };

      return await updateFn(wrappedTransaction);
    });
  }

  /**
   * Get current client timestamp (uses device clock)
   *
   * ⚠️ WARNING: Only use for UI display or client-side logic.
   * For server-synchronized timestamps in document writes, use fieldValue.serverTimestamp()
   *
   * @returns Firestore Timestamp based on client's system clock
   *
   * @example
   * // ❌ Bad - client clock may be wrong, breaks ordering
   * const checkIn = {
   *   id: generateId(),
   *   createdAt: this.db.clientTimestamp() // WRONG - use serverTimestamp!
   * };
   *
   * // ✅ Good - server assigns canonical timestamp
   * const checkIn = {
   *   id: generateId(),
   *   createdAt: this.db.fieldValue.serverTimestamp() // Correct
   * };
   *
   * // ✅ Good - client timestamp for UI display
   * const now = this.db.clientTimestamp();
   * const timeUntilCheckIn = triggerTime.toMillis() - now.toMillis();
   */
  clientTimestamp(): Timestamp {
    return Timestamp.now();
  }

  /**
   * Field value helpers for special Firestore operations
   *
   * **WI-028**: Uses unknown[] for arrayUnion/arrayRemove to match Firestore SDK
   * while maintaining type safety
   *
   * IMPORTANT: All helper functions are imported from firebase/firestore
   */
  get fieldValue(): FieldValueHelpers {
    return {
      serverTimestamp: (): FirestoreFieldValue => serverTimestamp(),
      delete: (): FirestoreFieldValue => deleteField(),
      increment: (n: number): FirestoreFieldValue => increment(n),
      arrayUnion: (...elements: unknown[]): FirestoreFieldValue => arrayUnion(...elements),
      arrayRemove: (...elements: unknown[]): FirestoreFieldValue => arrayRemove(...elements),
    };
  }

  // Private helper methods

  /**
   * Convert QueryConstraint[] to Firestore QueryConstraint[]
   */
  private buildQuery(constraints: QueryConstraint[]): FirestoreQueryConstraint[] {
    return constraints.map((c) => {
      switch (c.type) {
        case 'where':
          // TypeScript knows c.field, c.operator, c.value exist for type 'where'
          return where(c.field, c.operator, c.value);
        case 'orderBy':
          // TypeScript knows c.field exists for type 'orderBy'
          return orderBy(c.field, c.direction || 'asc');
        case 'limit':
          // TypeScript knows c.value is number for type 'limit'
          return limit(c.value);
        case 'startAfter':
          // TypeScript knows c.value exists for type 'startAfter'
          return startAfter(c.value);
        default:
          // WI-030c: Throw error for unknown constraint types
          // Cast to Record and use bracket notation for index signature access
          throw new Error(`Unknown query constraint type: ${(c as Record<string, unknown>)['type']}`);
      }
    });
  }

  /**
   * Extract collection name from document path
   */
  private extractCollection(path: string): string {
    const parts = path.split('/');
    return parts.length > 1 ? parts[parts.length - 2] : path;
  }

  /**
   * Extract document ID from document path
   */
  private extractDocId(path: string): string {
    const parts = path.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Handle and log errors with proper typing
   * H-02: Uses dev-logger for production PII protection
   *
   * @param operation - Operation name (e.g., 'getDoc', 'setDoc')
   * @param path - Document/collection path
   * @param error - Error object (Firebase, standard Error, or unknown)
   */
  private handleError(operation: string, path: string, error: unknown): void {
    // Extract error code with type safety
    let errorCode = 'UNKNOWN';
    if (typeof error === 'object' && error !== null && 'code' in error) {
      errorCode = String((error as { code: unknown }).code);
    }

    this.trackEvent('firestore.error', {
      operation,
      path,
      error_code: errorCode,
      timestamp: new Date().toISOString(),
    });

    devError(`[FirestoreService] ${operation} failed at ${path}:`, error);
  }

  /**
   * Track events for telemetry
   *
   * **WI-028**: Accepts unknown data type for flexible event tracking
   * **WI-039**: Integrated with Firebase Analytics
   *
   * H-02: Uses dev-logger for production PII protection
   *
   * @param eventName - Name of the event
   * @param data - Event metadata (must be Record<string, unknown> for analytics)
   */
  private trackEvent(eventName: string, data: unknown): void {
    // Log to dev console for debugging
    devLog(`[Firestore Event] ${eventName}`, data);

    // Log to Firebase Analytics if data is in correct format
    // Firestore events are low-level operations, so we only log errors for now
    // Feature-level analytics are tracked in respective services
    if (eventName.includes('error') && typeof data === 'object' && data !== null) {
      // Safe to cast since we checked it's an object
      const errorData = data as Record<string, unknown>;
      this.analytics.logEvent(eventName, errorData);
    }
  }
}
